/**
 * Tier → Feature Access Matrix
 *
 * A read-only reference table showing which features are included in each
 * subscription tier by default. No API calls — this is static configuration
 * that mirrors the business rules already enforced by the backend.
 *
 * Admins use this to:
 *  1. Quickly answer "what does Growth include?"
 *  2. Spot-check whether a company's manual overrides deviate from their tier
 *
 * If the tier defaults change, update TIER_DEFAULTS below.
 */

import React, { useState } from "react";
import { COMPANY_PERMISSIONS } from "../../interfaces/permissions";
import "../../assets/scss/pages/admin/_tierMatrix.scss";

// ─── Configuration ────────────────────────────────────────────────────────────

export const TIERS = [
	{ key: "free", label: "Free", color: "#6b7280" },
	{ key: "legacy", label: "Legacy", color: "#8b5cf6" },
	{ key: "supplier", label: "Supplier", color: "#3b82f6" },
	{ key: "fresh", label: "Fresh", color: "#10b981" },
	{ key: "growth", label: "Growth", color: "#f59e0b" },
	{ key: "enterprise", label: "Enterprise", color: "#ef4444" },
	{ key: "group", label: "Group", color: "#ec4899" },
	{ key: "guava", label: "Guava", color: "#06b6d4" }
] as const;

type TierKey = typeof TIERS[number]["key"];

/** true = included by default in that tier */
const TIER_DEFAULTS: Record<COMPANY_PERMISSIONS, Record<TierKey, boolean>> = {
	[COMPANY_PERMISSIONS.PACKAGING]: {
		free: false,
		legacy: true,
		supplier: false,
		fresh: true,
		growth: true,
		enterprise: true,
		group: true,
		guava: true
	},
	[COMPANY_PERMISSIONS.INGREDIENT_ENGINE]: {
		free: true,
		legacy: true,
		supplier: true,
		fresh: true,
		growth: true,
		enterprise: true,
		group: true,
		guava: true
	},
	[COMPANY_PERMISSIONS.PRODUCT_TOOLS]: {
		free: false,
		legacy: true,
		supplier: false,
		fresh: true,
		growth: true,
		enterprise: true,
		group: true,
		guava: true
	},
	[COMPANY_PERMISSIONS.PACKAGING_ENGINE]: {
		free: false,
		legacy: false,
		supplier: false,
		fresh: false,
		growth: true,
		enterprise: true,
		group: true,
		guava: true
	},
	[COMPANY_PERMISSIONS.COST_INSIGHTS]: {
		free: false,
		legacy: true,
		supplier: true,
		fresh: true,
		growth: true,
		enterprise: true,
		group: true,
		guava: true
	},
	[COMPANY_PERMISSIONS.REGULATORY_INSIGHTS]: {
		free: false,
		legacy: false,
		supplier: false,
		fresh: true,
		growth: true,
		enterprise: true,
		group: true,
		guava: true
	},
	[COMPANY_PERMISSIONS.SUSTAINABILITY_INSIGHTS]: {
		free: false,
		legacy: false,
		supplier: false,
		fresh: true,
		growth: true,
		enterprise: true,
		group: true,
		guava: true
	},
	[COMPANY_PERMISSIONS.PERSONA_ENGINE]: {
		free: false,
		legacy: false,
		supplier: false,
		fresh: false,
		growth: true,
		enterprise: true,
		group: true,
		guava: true
	},
	[COMPANY_PERMISSIONS.INGREDIENT_DATASHEET]: {
		free: false,
		legacy: true,
		supplier: true,
		fresh: true,
		growth: true,
		enterprise: true,
		group: true,
		guava: true
	},
	[COMPANY_PERMISSIONS.JOURNEY_AI]: {
		free: false,
		legacy: false,
		supplier: false,
		fresh: false,
		growth: false,
		enterprise: true,
		group: true,
		guava: true
	},
	[COMPANY_PERMISSIONS.CUSTOM_REPORTING]: {
		free: false,
		legacy: false,
		supplier: false,
		fresh: false,
		growth: true,
		enterprise: true,
		group: true,
		guava: true
	},
	[COMPANY_PERMISSIONS.INTEGRATION_REQUEST]: {
		free: false,
		legacy: false,
		supplier: false,
		fresh: false,
		growth: false,
		enterprise: true,
		group: true,
		guava: true
	},
	[COMPANY_PERMISSIONS.LABELS_TOOL]: {
		free: false,
		legacy: false,
		supplier: false,
		fresh: false,
		growth: true,
		enterprise: true,
		group: true,
		guava: true
	}
};

const PERMISSION_LABELS: Record<COMPANY_PERMISSIONS, string> = {
	[COMPANY_PERMISSIONS.PACKAGING]: "Packaging",
	[COMPANY_PERMISSIONS.INGREDIENT_ENGINE]: "Ingredient Engine",
	[COMPANY_PERMISSIONS.PRODUCT_TOOLS]: "Product Tools",
	[COMPANY_PERMISSIONS.PACKAGING_ENGINE]: "Packaging Engine",
	[COMPANY_PERMISSIONS.COST_INSIGHTS]: "Cost Insights",
	[COMPANY_PERMISSIONS.REGULATORY_INSIGHTS]: "Regulatory Insights",
	[COMPANY_PERMISSIONS.SUSTAINABILITY_INSIGHTS]: "Sustainability Insights",
	[COMPANY_PERMISSIONS.PERSONA_ENGINE]: "Persona Engine",
	[COMPANY_PERMISSIONS.INGREDIENT_DATASHEET]: "Ingredient Datasheet",
	[COMPANY_PERMISSIONS.JOURNEY_AI]: "Journey AI",
	[COMPANY_PERMISSIONS.CUSTOM_REPORTING]: "Custom Reporting",
	[COMPANY_PERMISSIONS.INTEGRATION_REQUEST]: "Integration Request",
	[COMPANY_PERMISSIONS.LABELS_TOOL]: "Labels Tool"
};

const ALL_PERMISSIONS = Object.values(COMPANY_PERMISSIONS);

// ─── Component ────────────────────────────────────────────────────────────────

const TierMatrix: React.FC = () => {
	const [highlightTier, setHighlightTier] = useState<TierKey | null>(null);

	// Count features per tier for the summary row
	const featureCount = (tier: TierKey) =>
		ALL_PERMISSIONS.filter((p) => TIER_DEFAULTS[p][tier]).length;

	return (
		<div className="tier-matrix">
			{/* ── Header ── */}
			<div className="tier-matrix__header">
				<div>
					<h3>Tier Feature Matrix</h3>
					<p>
						Default feature access per subscription tier. Individual companies can be overridden in
						the <strong>Tool Locking</strong> panel.
					</p>
				</div>
				<div className="tier-matrix__legend">
					<span className="tier-matrix__legend-item tier-matrix__legend-item--yes">
						<i className="fas fa-check" /> Included
					</span>
					<span className="tier-matrix__legend-item tier-matrix__legend-item--no">
						<i className="fas fa-times" /> Not included
					</span>
				</div>
			</div>

			{/* ── Scrollable table wrapper ── */}
			<div className="tier-matrix__table-wrap">
				<table className="tier-matrix__table" role="grid" aria-label="Tier feature access matrix">
					<thead>
						{/* Tier name row */}
						<tr>
							<th className="tier-matrix__feature-col" scope="col">
								Feature
							</th>
							{TIERS.map((tier) => (
								<th
									key={tier.key}
									scope="col"
									className={`tier-matrix__tier-col ${
										highlightTier === tier.key ? "tier-matrix__tier-col--highlight" : ""
									}`}
									onMouseEnter={() => setHighlightTier(tier.key)}
									onMouseLeave={() => setHighlightTier(null)}
								>
									<span className="tier-matrix__tier-badge" style={{ backgroundColor: tier.color }}>
										{tier.label}
									</span>
								</th>
							))}
						</tr>

						{/* Feature count summary row */}
						<tr className="tier-matrix__summary-row">
							<td className="tier-matrix__feature-col tier-matrix__summary-label">
								Features included
							</td>
							{TIERS.map((tier) => (
								<td
									key={tier.key}
									className={`tier-matrix__tier-col tier-matrix__summary-count ${
										highlightTier === tier.key ? "tier-matrix__tier-col--highlight" : ""
									}`}
									onMouseEnter={() => setHighlightTier(tier.key)}
									onMouseLeave={() => setHighlightTier(null)}
								>
									{featureCount(tier.key)}/{ALL_PERMISSIONS.length}
								</td>
							))}
						</tr>
					</thead>

					<tbody>
						{ALL_PERMISSIONS.map((permission, idx) => (
							<tr
								key={permission}
								className={`tier-matrix__row ${
									idx % 2 === 0 ? "tier-matrix__row--even" : "tier-matrix__row--odd"
								}`}
							>
								<td className="tier-matrix__feature-name" scope="row">
									{PERMISSION_LABELS[permission]}
								</td>
								{TIERS.map((tier) => {
									const included = TIER_DEFAULTS[permission][tier.key];
									return (
										<td
											key={tier.key}
											className={`tier-matrix__cell ${
												included ? "tier-matrix__cell--yes" : "tier-matrix__cell--no"
											} ${highlightTier === tier.key ? "tier-matrix__tier-col--highlight" : ""}`}
											onMouseEnter={() => setHighlightTier(tier.key)}
											onMouseLeave={() => setHighlightTier(null)}
											aria-label={`${PERMISSION_LABELS[permission]} — ${tier.label}: ${
												included ? "included" : "not included"
											}`}
										>
											{included ? (
												<i
													className="fas fa-check tier-matrix__icon tier-matrix__icon--yes"
													aria-hidden="true"
												/>
											) : (
												<i
													className="fas fa-times tier-matrix__icon tier-matrix__icon--no"
													aria-hidden="true"
												/>
											)}
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* ── Footer note ── */}
			<p className="tier-matrix__note">
				<i className="fas fa-info-circle" /> This matrix reflects default tier entitlements. Admins
				can grant or revoke individual features per company in the Tool Locking panel. Changes there
				do not affect this reference table.
			</p>
		</div>
	);
};

export default TierMatrix;
